/**
 * External busy blocks in the journal (boona docs/codebase/google-calendar/README.md §9.3, §11).
 *
 * Time a master holds outside Boona — in her own Google Calendar, in another calendar app (an ICS
 * feed) or in Booksy — reaches the shard as busy blocks. They close her online slots; the journal
 * draws them on her column and warns an admin who books over one (the admin may still book: the
 * journal's save skips the overlap guard). Only busy time ever arrives, never an event's title,
 * so the label is the journal's own: it says where the time is held.
 *
 * Pure (no React, no DOM), so the label mapping and the overlap check are tested on their own.
 */

/** One block as GET /api/v1/company/timetable/external_busy sends it. */
export type ExternalBusyBlockDTO = {
  id: number;
  staff_id: number | null;
  /** The salon-local day; one block never crosses midnight (the backend splits them). */
  date: string;
  /** Salon-local "HH:MM". */
  start: string | null;
  /** Salon-local "HH:MM"; a block that ends at midnight says "00:00". */
  end: string | null;
  length_sec: number;
  /** "google" (her own Google Calendar), "ics" (her other calendar app), "booksy". */
  provider: string;
  /** The backend's own wording. Not shown: the journal names the provider in the viewer's language. */
  label?: string;
  /** "active", or "conflict" when the block overlaps a live appointment. */
  status: string;
  conflict_record_id: number | null;
};

/** A block placed on the journal grid: minutes from the salon-local midnight of `date`. */
export type BusyBlock = {
  id: number;
  staffId: number;
  date: string;
  startMin: number;
  endMin: number;
  provider: string;
  /** It overlaps a live appointment (`conflictRecordId` names it). */
  conflict: boolean;
  conflictRecordId: number | null;
};

const DAY_MIN = 24 * 60;

type T = (key: string, fallback: string) => string;

/** "09:30" → 570. Anything that is not a time of day → NaN. */
export function hmToMinutes(value: string | null | undefined): number {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(value ?? "").trim());
  if (!match) return NaN;
  const total = Number(match[1]) * 60 + Number(match[2]);
  if (Number(match[2]) > 59 || total > DAY_MIN) return NaN;
  return total;
}

/**
 * The endpoint's block as the grid needs it, or null when it cannot be placed (no master, no day,
 * no start, no length). The end comes from the length: the `end` label reads "00:00" for a block
 * that runs to midnight, so it is only the fallback for a block sent without one.
 */
export function normalizeBusyBlock(raw: ExternalBusyBlockDTO | null | undefined): BusyBlock | null {
  if (!raw) return null;
  const staffId = Number(raw.staff_id);
  if (raw.staff_id == null || !Number.isFinite(staffId) || staffId <= 0) return null;
  const date = typeof raw.date === "string" ? raw.date.slice(0, 10) : "";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const startMin = hmToMinutes(raw.start);
  if (!Number.isFinite(startMin) || startMin >= DAY_MIN) return null;

  let endMin: number;
  const lengthSec = Number(raw.length_sec);
  if (raw.length_sec != null && Number.isFinite(lengthSec)) {
    endMin = startMin + Math.ceil(lengthSec / 60);
  } else {
    const end = hmToMinutes(raw.end);
    endMin = Number.isFinite(end) && end <= startMin ? DAY_MIN : end;
  }
  endMin = Math.min(endMin, DAY_MIN);
  if (!Number.isFinite(endMin) || endMin <= startMin) return null;

  return {
    id: Number(raw.id),
    staffId,
    date,
    startMin,
    endMin,
    provider: String(raw.provider ?? ""),
    conflict: raw.status === "conflict",
    conflictRecordId: raw.conflict_record_id ?? null,
  };
}

/**
 * The band's label: where the master's time is held. The backend's `label` is not used, so the
 * words follow the journal's language; a provider the journal does not know yet reads "Busy".
 */
export function busyLabel(provider: string | null | undefined, t: T): string {
  switch (provider) {
    case "google":
      return t("calendar.busy.google", "Busy · Google");
    case "ics":
      return t("calendar.busy.ics", "Busy · Calendar");
    case "booksy":
      return t("calendar.busy.booksy", "Busy · Booksy");
    default:
      return t("calendar.busy.other", "Busy");
  }
}

/** One cache bucket per master and day, the way the journal's columns are laid out. */
export function busyColumnKey(staffId: number, date: string): string {
  return `${staffId}|${date}`;
}

/** Blocks grouped by column (`busyColumnKey`), each column in start order. */
export function groupBusyBlocks(blocks: readonly BusyBlock[] | null | undefined): Map<string, BusyBlock[]> {
  const out = new Map<string, BusyBlock[]>();
  for (const block of blocks ?? []) {
    const key = busyColumnKey(block.staffId, block.date);
    const list = out.get(key);
    if (list) list.push(block);
    else out.set(key, [block]);
  }
  for (const list of out.values()) list.sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);
  return out;
}

/**
 * The part of a block the grid shows: the grid draws `dayStartMin`–`dayEndMin` only, and busy
 * time never stretches it (a day off in her calendar would otherwise open the journal 00:00–24:00).
 * Null when nothing of the block is inside the window.
 */
export function clipToDay(
  block: Pick<BusyBlock, "startMin" | "endMin">,
  dayStartMin: number,
  dayEndMin: number,
): { startMin: number; endMin: number } | null {
  const startMin = Math.max(block.startMin, dayStartMin);
  const endMin = Math.min(block.endMin, dayEndMin);
  return endMin > startMin ? { startMin, endMin } : null;
}

/**
 * Where a band's label can be read: the first minute of the band that no appointment card covers
 * (cards are drawn over the bands). A conflict block overlaps its appointment, so its label would
 * often hide behind that card; it moves to the first stretch the cards leave free. Null when the
 * cards cover the whole band (the conflict's red edge still shows in the card's gutter).
 */
export function firstUncoveredMinute(
  band: { startMin: number; endMin: number },
  cards: readonly { startMin: number; endMin: number }[] | null | undefined,
): number | null {
  let cursor = band.startMin;
  const over = (cards ?? [])
    .filter((card) => card.endMin > band.startMin && card.startMin < band.endMin)
    .sort((a, b) => a.startMin - b.startMin);
  for (const card of over) {
    if (card.startMin > cursor) break;
    cursor = Math.max(cursor, card.endMin);
  }
  return cursor < band.endMin ? cursor : null;
}

/**
 * A booking's minutes on its day from the modal's "HH:mm" fields. An end at or before the start
 * runs past midnight: on this day it ends at 24:00. Null while either time is missing.
 */
export function bookingMinutes(
  startTime: string | null | undefined,
  endTime: string | null | undefined,
): { startMin: number; endMin: number } | null {
  const startMin = hmToMinutes(startTime);
  const end = hmToMinutes(endTime);
  if (!Number.isFinite(startMin) || !Number.isFinite(end)) return null;
  return { startMin, endMin: end > startMin ? Math.min(end, DAY_MIN) : DAY_MIN };
}

/**
 * The blocks a booking would sit on: same master, same day, overlapping time. Back to back is not
 * an overlap (a 10:00–11:00 booking next to an 11:00 block is fine). Active and conflict blocks
 * both count — the time is busy in her calendar either way.
 */
export function busyOverlaps(
  blocks: readonly BusyBlock[] | null | undefined,
  booking: {
    staffId: number | null | undefined;
    date: string | null | undefined;
    startMin: number;
    endMin: number;
  },
): BusyBlock[] {
  const staffId = Number(booking.staffId);
  if (!blocks?.length || !booking.date || !Number.isFinite(staffId) || staffId <= 0) return [];
  if (!(booking.endMin > booking.startMin)) return [];
  return blocks.filter(
    (block) =>
      block.staffId === staffId &&
      block.date === booking.date &&
      block.startMin < booking.endMin &&
      block.endMin > booking.startMin,
  );
}
